/**
 * PromoTicker — landing-page social-proof block.
 *
 * Two stacked elements, both auto-rotating:
 *
 *   <ActivityTicker />        Three "live activity" cards, each on its
 *                             OWN rotation interval (staggered offsets
 *                             so the eye always catches motion in one
 *                             of the three without all three swapping
 *                             at once). Items pulled from
 *                             TICKER_ACTIVITY seed array.
 *
 *   <TestimonialCarousel />   One quote at a time, cross-fades every
 *                             ~7s. aria-live="polite" so assistive
 *                             tech announces each new quote without
 *                             interrupting flow.
 *
 * Reduced motion: when the user has `prefers-reduced-motion: reduce`
 * set at the OS level, fade durations collapse to 0 (instant swap),
 * and the green LIVE dot stops pulsing.
 *
 * Mounted by /app/frontend/src/pages/Landing.jsx between the hero CTA
 * and the features grid.
 */
import { useEffect, useMemo, useState } from "react";
import {
  Sparkles,
  Handshake,
  BadgeCheck,
  TrendingUp,
  Coins,
  Star,
  Users,
} from "lucide-react";

import { TICKER_ACTIVITY, TESTIMONIALS } from "@/lib/promo_seed_data";

// ─────────────────────────────────────────────────────────────────────
// Shared helpers
// ─────────────────────────────────────────────────────────────────────

function useReducedMotion() {
  // Returns the LATEST value of prefers-reduced-motion. Falls back to
  // `false` for SSR-style code paths where window is undefined.
  const [reduced, setReduced] = useState(
    typeof window !== "undefined" &&
      window.matchMedia?.("(prefers-reduced-motion: reduce)").matches,
  );
  useEffect(() => {
    if (typeof window === "undefined" || !window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const onChange = (e) => setReduced(e.matches);
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);
  return reduced;
}

const ICON_BY_TYPE = {
  analyzed:  Sparkles,
  counter:   Handshake,
  verdict:   BadgeCheck,
  portfolio: TrendingUp,
  fx:        Coins,
};

const VERDICT_TONE = {
  "Strong Buy": "border-emerald-200 bg-emerald-50 text-emerald-700",
  "Buy":        "border-emerald-200 bg-emerald-50/70 text-emerald-700",
  "Hold":       "border-amber-200 bg-amber-50 text-amber-800",
  "Pass":       "border-rose-200 bg-rose-50 text-rose-700",
};

function initials(name) {
  // "Maria T." → "MT", "Andre P." → "AP". Two-letter cap, robust to
  // single-word names.
  return (name || "?")
    .split(/[\s.]+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((s) => s[0]?.toUpperCase() || "")
    .join("") || "?";
}

// Deterministic soft pastel from a name string — gives each testimonial
// avatar a distinct but cohesive tile color without bundling images.
const PASTEL_PALETTE = [
  "from-emerald-100 to-emerald-200 text-emerald-800",
  "from-sky-100 to-sky-200 text-sky-800",
  "from-amber-100 to-amber-200 text-amber-800",
  "from-rose-100 to-rose-200 text-rose-800",
  "from-violet-100 to-violet-200 text-violet-800",
  "from-cyan-100 to-cyan-200 text-cyan-800",
];
function pastelFor(name) {
  let h = 0;
  for (let i = 0; i < (name || "").length; i += 1) {
    h = (h * 31 + name.charCodeAt(i)) | 0;
  }
  return PASTEL_PALETTE[Math.abs(h) % PASTEL_PALETTE.length];
}

// ─────────────────────────────────────────────────────────────────────
// Activity ticker — three independently-rotating slots
// ─────────────────────────────────────────────────────────────────────

function ActivityCard({ item, faded }) {
  const Icon = ICON_BY_TYPE[item.type] || Sparkles;
  return (
    <div
      data-testid="ticker-card"
      className={`flex min-w-0 items-center gap-3 rounded-2xl border border-slate-200 bg-white px-4 py-3 shadow-sm transition-opacity duration-500 ease-out ${
        faded ? "opacity-0" : "opacity-100"
      }`}
    >
      <span className="flex h-9 w-9 flex-none items-center justify-center rounded-xl bg-emerald-50 text-emerald-700">
        <Icon className="h-4 w-4" aria-hidden="true" />
      </span>
      <div className="min-w-0 flex-1">
        <p className="truncate text-xs font-semibold uppercase tracking-wider text-emerald-700">
          {item.label}
        </p>
        <p className="truncate text-sm font-medium text-slate-900">
          {item.subject}
        </p>
      </div>
      <div className="flex flex-none flex-col items-end gap-1">
        <span className="whitespace-nowrap text-xs font-semibold tabular-nums text-slate-700">
          {item.metric}
        </span>
        {item.verdict ? (
          <span
            className={`whitespace-nowrap rounded-full border px-2 py-0.5 text-[10px] font-semibold ${
              VERDICT_TONE[item.verdict] || "border-slate-200 bg-slate-50 text-slate-700"
            }`}
          >
            {item.verdict}
          </span>
        ) : null}
      </div>
    </div>
  );
}

function useRotatingIndex(length, intervalMs, offsetMs = 0, reduced = false) {
  const [idx, setIdx] = useState(() => Math.floor(Math.random() * length));
  useEffect(() => {
    if (!length) return undefined;
    const tick = () => setIdx((prev) => (prev + 1) % length);
    const startTimer = setTimeout(() => {
      tick();
      const handle = setInterval(tick, intervalMs);
      // store handle on the ref-like closure via the outer return
      // (we return a function that clears it below)
      cleanup.current = handle;
    }, offsetMs);
    const cleanup = { current: null };
    return () => {
      clearTimeout(startTimer);
      if (cleanup.current) clearInterval(cleanup.current);
    };
  }, [length, intervalMs, offsetMs]);
  // When reduced-motion is on, suppress the brief fade flicker by
  // returning a stable index that doesn't pulse "faded → opaque".
  // The fade is purely visual; data still rotates at the same cadence.
  return { idx, fadeOK: !reduced };
}

function TickerSlot({ items, intervalMs, offsetMs, reduced }) {
  const { idx, fadeOK } = useRotatingIndex(items.length, intervalMs, offsetMs, reduced);
  const [fade, setFade] = useState(false);

  useEffect(() => {
    if (!fadeOK) return undefined;
    // brief fade-out then fade-in around an index change
    setFade(true);
    const t = setTimeout(() => setFade(false), 280);
    return () => clearTimeout(t);
  }, [idx, fadeOK]);

  return <ActivityCard item={items[idx]} faded={fade && fadeOK} />;
}

function ActivityTicker() {
  const reduced = useReducedMotion();
  // Three slots, each with a slightly different cadence + start offset
  // so they never all swap at the same instant.
  const slotConfigs = useMemo(
    () => [
      { intervalMs: 3500, offsetMs: 0 },
      { intervalMs: 3900, offsetMs: 1100 },
      { intervalMs: 3700, offsetMs: 2300 },
    ],
    [],
  );
  return (
    <div
      data-testid="activity-ticker"
      className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3"
    >
      {slotConfigs.map((cfg, i) => (
        // index is a stable React key here — each slot owns its own
        // rotation state and never re-mounts, so the key is safe.
        // eslint-disable-next-line react/no-array-index-key
        <TickerSlot
          key={i}
          items={TICKER_ACTIVITY}
          intervalMs={cfg.intervalMs}
          offsetMs={cfg.offsetMs}
          reduced={reduced}
        />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Testimonial carousel — cross-fades every ~7s
// ─────────────────────────────────────────────────────────────────────

function TestimonialCard({ t, reduced }) {
  const tone = pastelFor(t.name);
  return (
    <article
      data-testid="testimonial-card"
      className={`relative rounded-2xl border border-slate-200 bg-white p-5 shadow-sm transition-opacity ${
        reduced ? "duration-0" : "duration-500"
      } ease-out sm:p-6`}
    >
      <div className="flex items-start gap-4">
        <div
          className={`flex h-12 w-12 flex-none items-center justify-center rounded-full bg-gradient-to-br ${tone} text-sm font-semibold shadow-inner`}
          aria-hidden="true"
        >
          {initials(t.name)}
        </div>
        <div className="min-w-0 flex-1">
          <div
            className="flex items-center gap-0.5 text-emerald-500"
            aria-label="5 out of 5 stars"
          >
            {[0, 1, 2, 3, 4].map((i) => (
              <Star key={i} className="h-3.5 w-3.5 fill-current" aria-hidden="true" />
            ))}
          </div>
          <p className="mt-2 text-sm leading-relaxed text-slate-700 sm:text-base">
            “{t.quote}”
          </p>
          <p className="mt-2 text-xs font-medium text-slate-500">
            <span className="font-semibold text-slate-900">{t.name}</span>
            <span className="mx-1.5 text-slate-300">·</span>
            {t.role}
          </p>
        </div>
      </div>
    </article>
  );
}

function TestimonialCarousel() {
  const reduced = useReducedMotion();
  const [idx, setIdx] = useState(0);
  const [fade, setFade] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      if (reduced) {
        setIdx((p) => (p + 1) % TESTIMONIALS.length);
      } else {
        // fade out, swap, fade in
        setFade(true);
        setTimeout(() => {
          setIdx((p) => (p + 1) % TESTIMONIALS.length);
          setFade(false);
        }, 320);
      }
    }, 7000);
    return () => clearInterval(interval);
  }, [reduced]);

  const current = TESTIMONIALS[idx];
  return (
    <div
      data-testid="testimonial-carousel"
      aria-live="polite"
      aria-atomic="true"
      className={`transition-opacity ${reduced ? "duration-0" : "duration-300"} ease-out ${
        fade && !reduced ? "opacity-0" : "opacity-100"
      }`}
    >
      <TestimonialCard t={current} reduced={reduced} />
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────
// Top-level export
// ─────────────────────────────────────────────────────────────────────

export default function PromoTicker() {
  const reduced = useReducedMotion();
  return (
    <section
      data-testid="promo-ticker"
      aria-label="Live activity and customer testimonials"
      className="border-y border-emerald-100 bg-emerald-50/30 py-10 sm:py-12"
    >
      <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-5 flex items-center justify-center gap-2 text-xs font-medium text-slate-600">
          <span className="relative flex h-2 w-2" aria-hidden="true">
            {!reduced ? (
              <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-75" />
            ) : null}
            <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
          </span>
          <Users className="h-3.5 w-3.5 text-slate-400" aria-hidden="true" />
          <span data-testid="promo-trust-line">
            Trusted by <span className="font-semibold text-slate-900">5,200+</span> investors
          </span>
        </div>
        <ActivityTicker />
        <div className="mt-6 sm:mt-8">
          <TestimonialCarousel />
        </div>
      </div>
    </section>
  );
}
